import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import {
  useCreateKnowledgeBaseCategoryMutation,
  useRemoveKnowledgeBaseCategoryMutation,
  useRenameKnowledgeBaseCategoryMutation,
  type KnowledgeBaseCategory,
} from "knowledge-base/_api/knowledge-base-api";
import type { KnowledgeBaseLibrary } from "knowledge-base/_schemas/knowledge-base-article-schema";
import { useState } from "react";

type Props = Readonly<{
  open: boolean;
  library: KnowledgeBaseLibrary;
  /** Absent when creating a new category. */
  category?: KnowledgeBaseCategory;
  /**
   * How many articles and subcategories are filed directly in this category. A category that holds
   * anything cannot be removed — the server refuses it — so the count is what turns Remove from a
   * button that fails into one that explains itself before it is pressed.
   */
  heldItems?: number;
  onClose: () => void;
}>;

/**
 * Creates or renames a category, and removes one that has been emptied.
 *
 * The description field is worth more than it looks. It is what a curator reads to decide where an
 * article goes, and it is what the summarizing agent will read when it has to place an uploaded
 * document on its own — a category described as "invoice formatting, LEDES codes, billing
 * timelines" gets sensible uploads; one called "Misc" with no description gets whatever is left.
 */
export default function CategoryEditorDialog({
  open,
  library,
  category,
  heldItems,
  onClose,
}: Props) {
  const isEditing = Boolean(category);
  const holds = heldItems ?? 0;
  const canRemove = holds === 0;

  const [name, setName] = useState(category?.name ?? "");
  const [description, setDescription] = useState(category?.description ?? "");
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const [createCategory, { isLoading: creating }] = useCreateKnowledgeBaseCategoryMutation();
  const [renameCategory, { isLoading: renaming }] = useRenameKnowledgeBaseCategoryMutation();
  const [removeCategory, { isLoading: removing }] = useRemoveKnowledgeBaseCategoryMutation();

  const busy = creating || renaming || removing;
  const canSave = name.trim().length > 0 && !busy;

  const save = async () => {
    setFailure(null);

    const request = category
      ? renameCategory({
          categoryId: category.id,
          name: name.trim(),
          // Sent even when empty. The server reads an absent description as "unchanged", so
          // dropping the empty string here made a description impossible to remove once written.
          description: description.trim(),
        })
      : createCategory({
          library,
          name: name.trim(),
          description: description.trim() || undefined,
        });

    const outcome = await request;

    if ("error" in outcome) {
      setFailure(messageFor(outcome.error));
      return;
    }

    onClose();
  };

  const remove = async () => {
    if (!category) return;

    setFailure(null);
    const outcome = await removeCategory(category.id);

    if ("error" in outcome) {
      // The usual refusal here is "this category still holds articles", which is a sentence the
      // reader needs to see rather than a generic failure.
      setFailure(messageFor(outcome.error));
      setConfirmingRemoval(false);
      return;
    }

    onClose();
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={busy ? undefined : onClose} open={open}>
      <DialogTitle>{isEditing ? "Edit category" : "New category"}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}

          <TextField
            fullWidth
            label="Name"
            onChange={(event) => setName(event.target.value)}
            placeholder="Billing Compliance"
            value={name}
          />

          <TextField
            fullWidth
            helperText="What belongs in here. This is what people — and the assistant that files uploaded documents — use to decide."
            label="Description"
            minRows={2}
            multiline
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Invoice formatting, LEDES codes, and billing timelines."
            value={description}
          />

          {confirmingRemoval && (
            <Alert severity="warning">
              <DialogContentText>
                Remove <strong>{category?.name}</strong>? It is empty, so nothing is lost with it,
                but it cannot be brought back.
              </DialogContentText>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        {isEditing ? (
          <Tooltip
            title={
              canRemove
                ? ""
                : `Move or delete what is filed here first — this category still holds ${holds} `
                    + `${holds === 1 ? "item" : "items"}.`
            }
          >
            {/*
              A disabled button fires none of the events a tooltip listens for, so the span is what
              the tooltip actually attaches to. Without it the explanation never appears, which is
              the whole point of disabling the button rather than letting it fail.
            */}
            <span>
              <Button
                color="error"
                disabled={busy || !canRemove}
                startIcon={<DeleteIcon />}
                onClick={() => {
                  if (confirmingRemoval) {
                    void remove();
                    return;
                  }
                  setConfirmingRemoval(true);
                }}
              >
                {confirmingRemoval ? "Confirm removal" : "Remove"}
              </Button>
            </span>
          </Tooltip>
        ) : (
          <span />
        )}

        <Stack direction="row" spacing={1}>
          <Button disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!canSave}
            onClick={() => void save()}
            startIcon={isEditing ? <SaveIcon /> : <AddIcon />}
            variant="contained"
          >
            {isEditing ? "Save" : "Create"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The server's own words where it gave any, and a plain sentence where it did not.
 *
 * Category refusals are explanatory — "still holds 4 article(s)", "a category named 'Billing' already
 * exists here" — and replacing them with a generic error would hide the one thing the reader needs
 * to know to get unstuck.
 */
function messageFor(error: unknown): string {
  if (error && typeof error === "object" && "data" in error) {
    const data = (error as { data: unknown }).data;
    if (typeof data === "string" && data.trim().length > 0) return data;
  }

  return "That could not be saved. Please try again.";
}
